import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AnalysisResult {
  symbol: string;
  timestamp: number;
  data: any; // Store the complete analysis data
}

interface AnalysisContextType {
  analysisResults: Record<string, AnalysisResult>;
  saveAnalysis: (symbol: string, data: any) => void;
  getAnalysis: (symbol: string) => AnalysisResult | null;
  clearAnalysis: (symbol: string) => void;
  clearAllAnalysis: () => void;
}

const AnalysisContext = createContext<AnalysisContextType | undefined>(undefined);

const STORAGE_KEY = 'crypto_analysis_results';
const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

export const AnalysisProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [analysisResults, setAnalysisResults] = useState<Record<string, AnalysisResult>>({});

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const now = Date.now();

        // Filter out expired results
        const filtered: Record<string, AnalysisResult> = {};
        Object.keys(parsed).forEach(key => {
          if (now - parsed[key].timestamp < MAX_AGE) {
            filtered[key] = parsed[key];
          }
        });

        setAnalysisResults(filtered);
      }
    } catch (error) {
      console.error('Failed to load analysis results:', error);
    }
  }, []);

  // Save to localStorage whenever results change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(analysisResults));
    } catch (error) {
      console.error('Failed to save analysis results:', error);
    }
  }, [analysisResults]);

  const saveAnalysis = (symbol: string, data: any) => {
    setAnalysisResults(prev => ({
      ...prev,
      [symbol.toUpperCase()]: {
        symbol: symbol.toUpperCase(),
        timestamp: Date.now(),
        data
      }
    }));
  };

  const getAnalysis = (symbol: string): AnalysisResult | null => {
    const result = analysisResults[symbol.toUpperCase()];
    if (!result) return null;

    // Check if expired
    if (Date.now() - result.timestamp > MAX_AGE) {
      clearAnalysis(symbol);
      return null;
    }

    return result;
  };

  const clearAnalysis = (symbol: string) => {
    setAnalysisResults(prev => {
      const newResults = { ...prev };
      delete newResults[symbol.toUpperCase()];
      return newResults;
    });
  };

  const clearAllAnalysis = () => {
    setAnalysisResults({});
    localStorage.removeItem(STORAGE_KEY);
  };

  // Reset on logout (explicit or token-expiry auto-logout) so the next account on
  // this device doesn't inherit this user's cached analysis results.
  useEffect(() => {
    const handleLogout = () => clearAllAnalysis();
    window.addEventListener('lumina:logout', handleLogout);
    return () => window.removeEventListener('lumina:logout', handleLogout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AnalysisContext.Provider
      value={{
        analysisResults,
        saveAnalysis,
        getAnalysis,
        clearAnalysis,
        clearAllAnalysis
      }}
    >
      {children}
    </AnalysisContext.Provider>
  );
};

export const useAnalysis = () => {
  const context = useContext(AnalysisContext);
  if (!context) {
    throw new Error('useAnalysis must be used within AnalysisProvider');
  }
  return context;
};
